import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getVendorBlogPosts, deleteBlogPost } from '../../services/firebase/blogService';
import './VendorBlogManagement.css';

const VendorBlogManagement = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, approved, pending, rejected
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadPosts();
  }, [user, navigate]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      console.log('Loading vendor posts for:', user.uid);
      const result = await getVendorBlogPosts(user.uid);
      console.log('Result:', result);
      
      if (result.success) {
        setPosts(result.data);
        setError('');
      } else {
        console.error('Failed to load posts:', result.error);
        setError(result.error || 'Failed to load posts');
      }
    } catch (err) {
      console.error('Error loading posts:', err);
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    try {
      const result = await deleteBlogPost(postId);
      if (result.success) {
        setPosts(posts.filter(p => p.id !== postId));
        setDeleteConfirm(null);
      } else {
        setError('Failed to delete post');
      }
    } catch (err) {
      setError('Error deleting post: ' + err.message);
    }
  };

  const filteredPosts = posts.filter(post => {
    if (filter === 'all') return true;
    return post.status === filter;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return '✅ Approved';
      case 'pending':
        return '⏳ Pending Review';
      case 'rejected':
        return '❌ Rejected';
      default:
        return status;
    }
  };

  const getStatusClass = (status) => {
    return `status-${status}`;
  };

  if (!user) {
    return null;
  }

  return (
    <div className="vendor-blog-container">
      <div className="vendor-blog-header">
        <div>
          <h1>📝 My Blog Posts</h1>
          <p>Manage your published and draft posts</p>
        </div>
        <button
          className="btn-create-new"
          onClick={() => navigate('/vendor/blog/create')}
        >
          ➕ Create New Post
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          ❌ {error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="blog-filters">
        {['all', 'approved', 'pending', 'rejected'].map(status => (
          <button
            key={status}
            className={`filter-btn ${filter === status ? 'active' : ''}`}
            onClick={() => setFilter(status)}
          >
            {status === 'all' && `All (${posts.length})`}
            {status === 'approved' && `✅ Approved (${posts.filter(p => p.status === 'approved').length})`}
            {status === 'pending' && `⏳ Pending (${posts.filter(p => p.status === 'pending').length})`}
            {status === 'rejected' && `❌ Rejected (${posts.filter(p => p.status === 'rejected').length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading your posts...</p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="empty-state">
          <p className="empty-icon">📭</p>
          <h3>No posts found</h3>
          <p>
            {filter === 'all'
              ? "You haven't created any blog posts yet."
              : `No ${filter} posts found.`}
          </p>
          <button
            className="btn-create-new"
            onClick={() => navigate('/vendor/blog/create')}
          >
            ✍️ Create Your First Post
          </button>
        </div>
      ) : (
        <div className="posts-grid">
          {filteredPosts.map(post => (
            <div key={post.id} className="post-card">
              {post.featuredImage && (
                <div className="post-image">
                  <img src={post.featuredImage} alt={post.title} />
                </div>
              )}

              <div className="post-content">
                <div className="post-meta">
                  <span className={`status-badge ${getStatusClass(post.status)}`}>
                    {getStatusBadge(post.status)}
                  </span>
                  <span className="post-date">
                    {new Date(post.createdAt?.toDate?.() || post.createdAt).toLocaleDateString()}
                  </span>
                </div>

                <h3 className="post-title">{post.title}</h3>
                <p className="post-excerpt">{post.excerpt}</p>

                <div className="post-stats">
                  <span>👁️ {post.viewCount || 0} views</span>
                  <span>💬 {post.comments || 0} comments</span>
                  <span>❤️ {post.likes || 0} likes</span>
                </div>

                {post.status === 'rejected' && post.rejectionReason && (
                  <div className="rejection-note">
                    <strong>Rejection Reason:</strong>
                    <p>{post.rejectionReason}</p>
                  </div>
                )}

                <div className="post-actions">
                  <button
                    className="btn-view"
                    onClick={() => navigate(`/blog/${post.id}`)}
                  >
                    👁️ View
                  </button>
                  <button
                    className="btn-edit"
                    onClick={() => navigate(`/vendor/blog/edit/${post.id}`)}
                    disabled={post.status !== 'pending'}
                    title={post.status !== 'pending' ? 'Only pending posts can be edited' : ''}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => setDeleteConfirm(post.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>

              {deleteConfirm === post.id && (
                <div className="delete-confirmation">
                  <p>Are you sure you want to delete this post?</p>
                  <div className="confirm-actions">
                    <button
                      className="btn-confirm-delete"
                      onClick={() => handleDelete(post.id)}
                    >
                      Yes, Delete
                    </button>
                    <button
                      className="btn-cancel-delete"
                      onClick={() => setDeleteConfirm(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VendorBlogManagement;
